import { useState } from 'react';
import { findPort } from '@hana/domain';
import { useT } from '@hana/i18n';
import { Chip } from '@hana/ui';
import { IconCheck, IconChevronDown, IconChevronUp, IconClock, IconPlugConnected, IconShip, IconAnchor } from '@tabler/icons-react';
import { dm, dmHm, usd } from './format';
import { freetimeLabel, type RateHit } from './p2p';
import type { P2PBuyRate, P2PRate, Sailing } from './types';
import './P2PPadCard.i18n';
import s from './p2ppad.module.css';

const cityOf = (code: string) => findPort(code)?.name ?? code;
const ddmm = (iso: string | null) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '—');
const WD = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
const weekday = (iso: string) => WD[new Date(`${iso.slice(0, 10)}T12:00:00Z`).getUTCDay()] ?? '';

interface Props {
  hit: RateHit;
  /** giá hiện theo tab (bậc 1 / bậc 2 / mua / mua + margin) */
  sell: readonly [number, number];
  cost?: P2PBuyRate;
  caption: string;
  codes: readonly [string, string];
  picked: boolean;
  onPick: () => void;
  sailings: readonly Sailing[];
  /** Khổ desktop (Mint-P2P · -1920): thẻ gọn hơn, trục POL → POD rộng 340. */
  desk?: boolean;
}

/** Thẻ giá của khổ iPad / điện thoại (board Mint-P2P-iPad, bước kết quả): ô chọn vào báo giá · trục POL → POD · hãng + dịch vụ · hiệu lực · free time · giá. */
export function PadRateCard({ hit, sell, cost, caption, codes, picked, onPick, sailings, desk }: Props) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const r: P2PRate = hit.rate;
  const left = hit.daysLeft;
  const warn = left >= 0 && left <= 7;
  const via = r.via ? t('pd.via', { v: r.via }) : t('pd.direct');
  const buys = cost ? [cost.buy20, cost.buy40] : null;
  return (
    <article className={`${s.card} ${picked ? s.picked : ''} ${open ? s.open : ''} ${desk ? s.desk : ''}`} aria-label={r.carrierName} data-expired={left < 0 || undefined}>
      {desk ? (
        <span role="checkbox" tabIndex={0} aria-checked={picked} aria-label={t('pd.pick', { v: r.carrierName })} title={t('pd.pickTip')} className={s.tick} onClick={onPick} onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onPick(); } }}>
          <span className={s.box}>{picked && <IconCheck size={14} aria-hidden="true" />}</span>
        </span>
      ) : (
        <button type="button" role="checkbox" aria-checked={picked} aria-label={t('pd.pick', { v: r.carrierName })} title={t('pd.pickTip')} className={s.tick} onClick={onPick}>
          <span className={s.box}>{picked && <IconCheck size={14} aria-hidden="true" />}</span>
        </button>
      )}
      <div className={s.main}>
        <div className={s.axis} role="img" aria-label={t('pd.ttAria', { a: r.pol, b: r.pod, n: r.transitDays, m: r.via ? t('pd.viaAria', { v: r.via }) : t('pd.directAria') })}>
          <div className={s.pt}>
            <small>POL</small>
            <b>{r.pol}</b>
            <span title={cityOf(r.pol)}>{cityOf(r.pol)}</span>
          </div>
          <div className={s.mid}>
            <b>{t('pd.tt', { n: r.transitDays })}</b>
            <div className={s.line}><i /><u /><em /></div>
            <span className={r.via ? s.viaWarn : undefined}>{via}</span>
          </div>
          <div className={`${s.pt} ${s.ptEnd}`}>
            <small>POD</small>
            <b>{r.pod}</b>
            <span title={cityOf(r.pod)}>{cityOf(r.pod)}</span>
          </div>
        </div>
        <div className={s.info}>
          <small>{t('pd.carSvc')}</small>
          <div className={s.carRow}>
            <b>{r.carrier}</b>
            <span title={r.carrierName}>{r.carrierName}</span>
            {hit.cheapest && <em className={s.best} title={t('p2.cheapestTip')}>{t('p2.cheapest')}</em>}
          </div>
          <div className={s.dim}>{r.service ?? t('pd.svcUnmapped')} · {t('pd.weekly', { d: weekday(r.etd) })}</div>
          <div className={s.validRow}>
            <span className={s.dim}>{t('pd.valid')}</span>
            <b className={left < 0 ? s.err : undefined}>{t('pd.until', { d: ddmm(r.validEnd) })}</b>
            <b className={left < 0 ? s.err : warn ? s.warn : s.lb}>{left < 0 ? t('pd.expired') : left === 0 ? t('pd.leftToday') : t('pd.left', { n: left })}</b>
          </div>
          <div className={s.dim} title={t('pd.freeTip', { v: r.freetime })}>{t('pd.free')} <b>{freetimeLabel(r.freetime)}</b></div>
        </div>
        <div className={s.price}>
          <small>{caption}</small>
          <div className={s.pr2}>
            {sell.map((v, i) => (
              <div key={codes[i]}>
                <small>{codes[i]}</small>
                <b>{usd(v)}</b>
                {buys && <span className={v < (buys[i] ?? 0) ? s.warn : s.lb}>{t('p2.buy', { v: usd(buys[i] ?? 0) })}</span>}
              </div>
            ))}
          </div>
          {buys && sell.some((v, i) => v < (buys[i] ?? 0)) && <em className={s.below}>{t('pd.below')}</em>}
        </div>
        <button type="button" className={s.exp} aria-expanded={open} aria-label={t('pd.open', { v: r.carrierName })} title={t('pd.openTip')} onClick={() => setOpen(!open)}>
          {open ? <IconChevronUp size={18} aria-hidden="true" /> : <IconChevronDown size={18} aria-hidden="true" />}
        </button>
      </div>
      {open && (
        <div className={s.detail}>
          <Chip icon={<IconPlugConnected size={13} />} label={t('p2.free')}>{freetimeLabel(r.freetime)}</Chip>
          <Chip icon={<IconShip size={13} />} label={t('p2.next')}>{r.nextVoyage} · ETD {dm(r.etd)}</Chip>
          <Chip icon={<IconClock size={13} />} label={t('p2.cy')}>{dmHm(r.cyCutoff)}</Chip>
          {sailings.slice(0, 3).map((v) => (
            <span key={v.id} className={s.dim}><IconAnchor size={12} aria-hidden="true" /> {v.vessel} {v.voyage} · ETD {dm(v.etd)} → ETA {dm(v.eta)}</span>
          ))}
        </div>
      )}
    </article>
  );
}
